import { useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';

export enum ApolloErrorKind {
  NetworkError = 'NetworkError',
  PermissionDenied = 'PermissionDenied',

  NotFound = 'NotFound',

  InternalServerError = 'InternalError',

  NotApolloError = 'NotApolloError',
}

/**
 *
 * useApolloError
 *
 * An error hook that will translate an ApolloError into a message that can be
 * shown to the user. This will allow us to show messages such as "Permission
 * Denied" instead of a graphql error.
 *
 */
export default function useApolloError(
  error: ApolloError | string | Error | object,
) {
  const [errorKind, setErrorKind] = useState<ApolloErrorKind>(
    ApolloErrorKind.NotApolloError,
  );

  useEffect(() => {
    // only do this if it is an apollo error, ignore everything else
    if (error instanceof ApolloError) {
      // for network errors, do not log
      if (error.networkError) {
        setErrorKind(ApolloErrorKind.NetworkError);
        return;
      }

      // for errors that are related to Authorization, do not log
      if (error?.message?.match(/Not Authorized to access/i)) {
        setErrorKind(ApolloErrorKind.PermissionDenied);
        return;
      }

      // for graphql errors, we need to check the type of error
      // if it's a validation error, do not log
      if (error?.message?.match(/not found!/!)) {
        setErrorKind(ApolloErrorKind.NotFound);
        return;
      }

      // When an item isn't found and appsync returns an error like the
      // following Cannot return null user of Type Users
      if (error?.message?.match(/Cannot return null/i)) {
        setErrorKind(ApolloErrorKind.NotFound);
        return;
      }

      setErrorKind(ApolloErrorKind.InternalServerError);
      return;
    }

    // not an apollo error, so just set the error kind to NotApolloError
    setErrorKind(ApolloErrorKind.NotApolloError);
  }, [error]);

  return errorKind;
}
